import type { DirectiveBinding } from 'vue'
import { envConfig } from '@/config/env'
interface LazyLoadOptions {
    threshold?: number
    rootMargin?: string // IntersectionObserver的根边距配置，用于扩展根元素的边界框来提前或延迟触发懒加载
}

const isDev = envConfig.env === 'development'
const observerMap = new WeakMap<Element, IntersectionObserver>()

// 创建观察器的函数
const createObserver = (el: HTMLElement, options: LazyLoadOptions) => {
    const { threshold = 0.1, rootMargin = '50px' } = options

    const observer = new IntersectionObserver(
        (entries) => {
            entries.forEach((entry) => {
                // 调试信息：交集变化
                if (isDev) {
                    console.log('👁️ [LazyLoad] 元素可见性变化:', {
                        element: el,
                        isIntersecting: entry.isIntersecting,
                        intersectionRatio: entry.intersectionRatio,
                        isLoaded: el.getAttribute('data-lazy-loaded'),
                        id: el.id || el.className
                    })
                }

                if (entry.isIntersecting && el.getAttribute('data-lazy-loaded') === 'false') {
                    // 标记为已加载
                    el.setAttribute('data-lazy-loaded', 'true')

                    // 调试信息：触发加载
                    if (isDev) {
                        console.log('✅ [LazyLoad] 触发懒加载:', {
                            element: el,
                            id: el.id || el.className,
                            timestamp: new Date().toLocaleTimeString()
                        })
                    }

                    // 触发自定义事件
                    el.dispatchEvent(new CustomEvent('lazy-load', {
                        detail: { element: el }
                    }))

                    // 停止观察
                    observer.unobserve(el)
                    observerMap.delete(el)

                    if (isDev) {
                        console.log('🛑 [LazyLoad] 停止观察元素:', {
                            element: el,
                            id: el.id || el.className
                        })
                    }
                }
            })
        },
        {
            threshold,
            rootMargin
        }
    )

    return observer
}

const vLazyLoad = {
    mounted(el: HTMLElement, binding: DirectiveBinding<LazyLoadOptions>) {
        const options = binding.value || {}

        // 添加一个数据属性来标记是否已加载
        el.setAttribute('data-lazy-loaded', 'false')

        // 调试信息：元素开始观察
        if (isDev) {
            console.log('🔍 [LazyLoad] 开始观察元素:', {
                element: el,
                threshold: options.threshold || 0.1,
                rootMargin: options.rootMargin || '50px',
                id: el.id || el.className
            })
        }

        const observer = createObserver(el, options)
        observer.observe(el)
        observerMap.set(el, observer)

        // 存储配置选项，用于重新观察
        el.dataset.lazyLoadOptions = JSON.stringify(options)
    },

    updated(el: HTMLElement, binding: DirectiveBinding<LazyLoadOptions>) {
        // 如果元素被标记为 false 但没有观察器，重新创建观察器
        if (el.getAttribute('data-lazy-loaded') === 'false' && !observerMap.has(el)) {
            const options = binding.value || {}

            if (isDev) {
                console.log('🔄 [LazyLoad] 重新观察元素:', {
                    element: el,
                    id: el.id || el.className
                })
            }

            const observer = createObserver(el, options)
            observer.observe(el)
            observerMap.set(el, observer)
        }
    },

    unmounted(el: HTMLElement) {
        const observer = observerMap.get(el)
        if (observer) {
            if (isDev) {
                console.log('🧹 [LazyLoad] 清理观察器:', {
                    element: el,
                    id: el.id || el.className
                })
            }
            observer.unobserve(el)
            observer.disconnect()
            observerMap.delete(el)
        }
        el.removeAttribute('data-lazy-loaded')
        delete el.dataset.lazyLoadOptions
    }
}

export default vLazyLoad 